import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import UserService from '../../services/userService'
import { Product, ProductPriceResponse } from '../../types/product'
import { getDefaultVariant, getEffectivePrice } from '../../utils/cartUtils'
import { mapLikedApiProductToWishlistItem } from './wishlistMapping'

export interface WishlistItem {
  id: string
  name: string
  price: number
  mainPicture?: string
  slug: string
  description?: string
  beanType?: { label: string; value: string }
  originCountry?: string
  originRegion?: string
  prices?: ProductPriceResponse[]
  variants?: Array<{ label: string; value: string }>
  tags?: string[]
}

interface WishlistState {
  items: WishlistItem[]
  isLoading: boolean
  error: string | null
  addedItemNotification: {
    item: WishlistItem | null
    show: boolean
  }
}

// Функції для роботи з localStorage
const loadWishlistFromLocalStorage = (): WishlistItem[] => {
  try {
    const serializedWishlist = localStorage.getItem('wishlist')
    return serializedWishlist ? JSON.parse(serializedWishlist) : []
  } catch (e) {
    return []
  }
}

const saveWishlistToLocalStorage = (wishlist: WishlistItem[]) => {
  try {
    const serializedWishlist = JSON.stringify(wishlist)
    localStorage.setItem('wishlist', serializedWishlist)
  } catch (e) {
    // Silently fail
  }
}

// Async thunks for API calls
export const fetchUserWishlist = createAsyncThunk(
  'wishlist/fetchUserWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getUserLikedProducts()

      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to fetch liked products')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (payload: { productId: string; product: Product }, { rejectWithValue }) => {
    try {
      const response = await UserService.addToLiked(payload.productId)
      if (response.success) {
        return { productId: payload.productId, id: response.data?.id, product: payload.product }
      } else {
        return rejectWithValue(response.error || 'Failed to add to liked products')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await UserService.removeFromLiked(productId)
      if (response.success) {
        return productId
      } else {
        return rejectWithValue(response.error || 'Failed to remove from liked products')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

// Sync local wishlist items to backend when user logs in
export const syncLocalWishlistToBackend = createAsyncThunk(
  'wishlist/syncLocalWishlistToBackend',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const localItems = loadWishlistFromLocalStorage()

      // If there are no local items, just fetch from backend
      if (localItems.length === 0) {
        await dispatch(fetchUserWishlist())
        return { synced: 0 }
      }

      // Add each local item to the backend
      const syncPromises = localItems.map(item => UserService.addToLiked(item.id))

      await Promise.allSettled(syncPromises)

      // Fetch updated wishlist from backend
      await dispatch(fetchUserWishlist())

      return { synced: localItems.length }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

const initialState: WishlistState = {
  items: loadWishlistFromLocalStorage(),
  isLoading: false,
  error: null,
  addedItemNotification: {
    item: null,
    show: false,
  },
}

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    addToWishlistLocal: (state, action: PayloadAction<Product>) => {
      const product = action.payload
      const existingItem = state.items.find(item => item.id === product.id)

      if (!existingItem) {
        const defaultVariant = getDefaultVariant(product.prices || [])
        const wishlistItem: WishlistItem = {
          id: product.id,
          name: product.name,
          price: defaultVariant ? getEffectivePrice(defaultVariant) : 0,
          mainPicture: product.mainPicture,
          slug: product.slug,
          description: product.description,
          beanType: product.beanType,
          originCountry: product.originCountry,
          originRegion: product.originRegion,
          prices: product.prices,
          variants: product.variants,
          tags: product.tags?.map(tag => (typeof tag === 'string' ? tag : tag.name)) || undefined,
        }
        state.items.push(wishlistItem)
        saveWishlistToLocalStorage(state.items)

        // Показуємо нотіфікейшн про додавання до вішлисту
        state.addedItemNotification = {
          item: wishlistItem,
          show: true,
        }
      }
    },
    removeFromWishlistLocal: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload)
      saveWishlistToLocalStorage(state.items)
    },
    clearWishlistLocal: state => {
      state.items = []
      saveWishlistToLocalStorage(state.items)
    },
    setWishlistItems: (state, action: PayloadAction<WishlistItem[]>) => {
      state.items = action.payload
      saveWishlistToLocalStorage(state.items)
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    // Actions для управління повідомленням про додавання товару до вішлисту
    showAddedItemNotification: (state, action: PayloadAction<WishlistItem>) => {
      state.addedItemNotification = {
        item: action.payload,
        show: true,
      }
    },
    hideAddedItemNotification: state => {
      state.addedItemNotification = {
        item: null,
        show: false,
      }
    },
  },
  extraReducers: builder => {
    // Fetch user wishlist
    builder
      .addCase(fetchUserWishlist.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUserWishlist.fulfilled, (state, action) => {
        state.isLoading = false
        // Map API response to local format with null checks

        if (!Array.isArray(action.payload)) {
          state.items = []
          return
        }

        const wishlistItems: WishlistItem[] = action.payload
          .filter(item => item && item.id)
          .map(mapLikedApiProductToWishlistItem)
        state.items = wishlistItems
        saveWishlistToLocalStorage(state.items)
      })
      .addCase(fetchUserWishlist.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Add to wishlist
    builder
      .addCase(addToWishlist.pending, (state, action) => {
        // Don't set isLoading to avoid modal jumping/re-rendering
        // We do optimistic update instead
        state.error = null
        // Optimistic update - add to local state immediately
        const { productId, product } = action.meta.arg
        const existingItem = state.items.find(item => item.id === productId)
        if (!existingItem) {
          // Create wishlist item with product data
          const defaultVariant = getDefaultVariant(product.prices || [])
          const wishlistItem: WishlistItem = {
            id: product.id,
            name: product.name,
            price: defaultVariant ? getEffectivePrice(defaultVariant) : 0,
            mainPicture: product.mainPicture,
            slug: product.slug,
            description: product.description,
            beanType: product.beanType,
            originCountry: product.originCountry,
            originRegion: product.originRegion,
            prices: product.prices,
            variants: product.variants,
            tags: product.tags?.map(tag => (typeof tag === 'string' ? tag : tag.name)) || undefined,
          }
          state.items.push(wishlistItem)
          saveWishlistToLocalStorage(state.items)
        }
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        // Show notification for authenticated users
        const { product } = action.meta.arg
        const item = state.items.find(item => item.id === product.id)
        if (item) {
          state.addedItemNotification = {
            item: item,
            show: true,
          }
        }
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.error = action.payload as string
        // Rollback optimistic update
        const { productId } = action.meta.arg
        state.items = state.items.filter(item => item.id !== productId)
        saveWishlistToLocalStorage(state.items)
      })

    // Remove from wishlist
    builder
      .addCase(removeFromWishlist.pending, (state, action) => {
        // Don't set isLoading to avoid modal jumping/re-rendering
        // We do optimistic update instead
        state.error = null
        // Optimistic update - remove from local state immediately
        const productId = action.meta.arg
        state.items = state.items.filter(item => item.id !== productId)
        saveWishlistToLocalStorage(state.items)
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        // Item already removed optimistically, no need to do anything
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.error = action.payload as string
        // On error, refetch to restore correct state
        // The item will reappear on next fetch or page refresh
      })
  },
})

export const {
  addToWishlistLocal,
  removeFromWishlistLocal,
  clearWishlistLocal,
  setWishlistItems,
  setLoading,
  setError,
  showAddedItemNotification,
  hideAddedItemNotification,
} = wishlistSlice.actions

export default wishlistSlice.reducer

// Selectors
export const selectWishlistItems = (state: { wishlist: WishlistState }) => state.wishlist.items
export const selectWishlistLoading = (state: { wishlist: WishlistState }) =>
  state.wishlist.isLoading
export const selectWishlistError = (state: { wishlist: WishlistState }) => state.wishlist.error
export const selectIsInWishlist = (productId: string) => (state: { wishlist: WishlistState }) =>
  state.wishlist.items.some(item => item.id === productId)
export const selectWishlistCount = (state: { wishlist: WishlistState }) =>
  state.wishlist.items.length
export const selectAddedItemNotification = (state: { wishlist: WishlistState }) =>
  state.wishlist.addedItemNotification
